import axios, { AxiosRequestConfig } from 'axios';
import * as storage from 'utils/storage';
import { getAuthorizationHeader } from 'auth/auth-helpers';

const httpClient = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  headers: { 'content-type': 'application/json' },
});

type MyAxiosRequestConfig = AxiosRequestConfig & { _loginByRefreshToken?: boolean };

export const refreshTokens = async () => {
  const refreshTokenFromStorage = JSON.parse(storage.getStorageItem('refreshToken', '{}'));

  const {
    data: { accessToken, refreshToken },
  } = await httpClient.post(
    `${process.env.REACT_APP_API_URL}/auth/login-by-refresh-token`,
    {
      refreshToken: refreshTokenFromStorage,
    },
    {
      _loginByRefreshToken: true,
    } as MyAxiosRequestConfig,
  );
  storage.setStorageItem('accessToken', JSON.stringify(accessToken));
  storage.setStorageItem('refreshToken', JSON.stringify(refreshToken));
  return {
    accessToken,
    refreshToken,
  };
};

let isRefreshing = false;
let failedQueue: any[] = [];

const processQueue = ({ error, accessToken }: { error?: any; accessToken?: string }) => {
  failedQueue.forEach((promise: any) => {
    if (error) {
      false && console.log('rejecting request that failed while refreshing', error);
      promise.reject(error);
    } else {
      false &&
        console.log(
          're-running request that failed while refreshing with new access token',
          accessToken,
        );
      promise.resolve(accessToken);
    }
  });

  failedQueue = [];
};

httpClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (
      !originalRequest._retry &&
      !originalRequest._loginByRefreshToken &&
      (error.response?.status === 401 || error.response?.status === 500)
    ) {
      false &&
        console.log('request failed with 401 or 500 (not retried and not login-by-refresh-token');

      if (isRefreshing) {
        false && console.log('another request failed while isRefreshing');
        const token = await new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        });
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return await axios(originalRequest);
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        return await axios(originalRequest);
      } catch (error) {
        false && console.log('error from login-by-refresh-token');
        processQueue({ error });
        throw error;
      } finally {
        false && console.log('isRefreshing = false');
        isRefreshing = false;
      }
    }
    throw error;
  },
);

/*
 * Function helper returned function that return promise
 * @param method {string} api method (get, put, post, etc...)
 * @param url {string|function} url to api method
 *
 */
export const makeAxiosRequest =
  (url: string, method: { method: string }, auth: boolean = false, body: any = null) =>
  (
    { headers, ...argsRest }: { headers?: Record<string, any> } = {
      headers: undefined,
    },
  ) =>
    httpClient({
      url,
      ...method,
      ...((headers || auth) && {
        headers: {
          ...(auth && {
            'x-blu-jwt': getAuthorizationHeader(),
          }),
          ...headers,
        },
      }),
      ...(body && { data: JSON.stringify(body) }),
      ...argsRest,
    });
